import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, useApi } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { setPendingJoin, clearPendingJoin } from '../lib/pendingJoin';
import '../Dashboard.css';

// Public landing page for circle invite links: sphere-sync.com/join/<CODE>.
// Anyone can open it — it previews the circle via the public validate endpoint, then
// routes the visitor to sign in / create an account, or joins directly when already
// signed in. An expired/rotated code gets a clear "invite is invalid" state.
export default function JoinCircle() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const cleanCode = String(code || '').trim().toUpperCase();

  const { data, loading, error } = useApi(`/crews/invite/${encodeURIComponent(cleanCode)}`);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);

  const invalid = !loading && (error || !data?.valid);
  const circle = data?.circle;
  const authed = !authLoading && user && user.emailVerified;

  // Remember this code for the whole sign-up journey: if the visitor has no account yet,
  // it survives register → email-verify → sign-in (even in a new tab) and the dashboard
  // auto-joins them on arrival. Cleared on a successful join or an invalid code.
  useEffect(() => {
    if (cleanCode) setPendingJoin(cleanCode);
  }, [cleanCode]);

  const onJoin = async () => {
    setJoining(true);
    setJoinError(null);
    try {
      const res = await api.post('/crews/join', { inviteCode: cleanCode });
      clearPendingJoin();
      const crewId = res.data?.crew?.id;
      navigate(crewId ? `/app/circles/${crewId}` : '/app/circles', { replace: true });
    } catch (err) {
      const codeErr = err?.response?.data?.error;
      if (codeErr === 'crew_not_found') clearPendingJoin();
      setJoinError(
        codeErr === 'blocked_from_circle'
          ? 'You’ve been blocked from this circle by its admins.'
          : codeErr === 'crew_not_found'
            ? 'This invite code is no longer valid.'
            : 'Could not join right now. Check your connection and try again.'
      );
    } finally {
      setJoining(false);
    }
  };

  // Signed-in visitors with a valid code join automatically — the link IS the action.
  useEffect(() => {
    if (authed && data?.valid && !joining && !joinError) onJoin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, data?.valid]);

  const loginState = { state: { from: { pathname: `/join/${cleanCode}` } } };

  return (
    <div className="db-auth-page">
      <div className="db-auth-bg" aria-hidden="true">
        <span className="db-glow db-glow-a" />
        <span className="db-glow db-glow-b" />
      </div>
      <div className="db-auth-card">
        <Link to="/" className="db-auth-brand">
          <img className="db-mark" src="/spheresync-icon.png" alt="" aria-hidden="true" />
          Sphere<span className="db-grad">Sync</span>
        </Link>

        {loading || authLoading ? (
          <p className="db-auth-sub">Checking your invite…</p>
        ) : invalid ? (
          <>
            <h1 className="db-auth-title">Invite code is invalid</h1>
            <p className="db-auth-sub">
              This invite is no longer valid. The circle owner may have generated a new code —
              ask them for a fresh link.
            </p>
            <Link to="/" className="db-btn db-btn-block" style={{ textAlign: 'center' }}>Back to SphereSync</Link>
            <p className="db-auth-foot">
              Curious what SphereSync is? <Link to="/" className="db-auth-link">See how it works</Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="db-auth-title">Join {circle.name}</h1>
            <p className="db-auth-sub">
              You&rsquo;ve been invited to a SphereSync circle
              {circle.memberCount ? ` with ${circle.memberCount} member${circle.memberCount === 1 ? '' : 's'}` : ''}.
            </p>
            {/* three-beat pitch (MARKETING.md §2.4) — this page converts better than the homepage */}
            <p className="db-auth-sub">
              <strong>{circle.name} uses SphereSync to plan what&rsquo;s next and keep what happened.</strong>{' '}
              Join the circle to see the moments they&rsquo;re planning, vote on where and when, and
              be part of the memories they keep — photos, messages, and who was there, all in one
              private place. No public feed. No strangers. Just this circle.
            </p>

            {joinError && <div className="db-auth-error">{joinError}</div>}

            {authed ? (
              <button className="db-btn db-btn-primary db-btn-block" onClick={onJoin} disabled={joining}>
                {joining ? 'Joining…' : `Join ${circle.name}`}
              </button>
            ) : (
              <>
                <Link to="/register" {...loginState} className="db-btn db-btn-primary db-btn-block" style={{ textAlign: 'center' }}>
                  Create account &amp; join
                </Link>
                <Link to="/login" {...loginState} className="db-btn db-btn-block" style={{ textAlign: 'center', marginTop: 10 }}>
                  I already have an account
                </Link>
              </>
            )}

            <div className="db-join-perks" aria-hidden="true">
              <span className="db-join-perk"><i>✓</i>Plan together</span>
              <span className="db-join-perk"><i>◉</i>Capture it live</span>
              <span className="db-join-perk"><i>∞</i>Keep it forever</span>
            </div>

            <p className="db-auth-foot">
              Have the Android app? <a href={`spheresync://join/${cleanCode}`}>Open this invite in the app</a> —
              in-person verification and live check-ins happen there.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
